import Result from '@/modules/core/models/result.class.ts'
import { ApiError } from '@/modules/core/models/api-error.class.ts'
import { ApiErrorType } from '@/modules/core/enums/api-error-type.ts'
import type {
  ListResponse,
  ObjectResponse,
} from '@/modules/core/interfaces/api-response.interfaces.ts'

export abstract class BaseApiService {
  protected handleApiError(error: unknown): Result<never> {
    logError('ApiService::Error:', error)
    if (error instanceof ApiError) {
      return Result.failure(error)
    }
    return Result.failure(
      new ApiError({
        type: ApiErrorType.NETWORK_ERROR,
        message: 'Unexpected error occurred',
        retryable: false,
        userMessage: 'Something went wrong. Please try again.',
      })
    )
  }

  /**
   * Handles successful API responses by converting them to Result objects
   * Note: Allows null data for operations like DELETE that don't return data
   */
  protected handleResponse<T>(response: ObjectResponse<T> | ListResponse<T>): Result<T | T[]> {
    if (response.status) {
      // Allow null data for successful responses (e.g., DELETE operations)
      return Result.success(response.data)
    } else {
      // Check if it's a validation-type error (e.g., duplicate entries, constraint violations)
      const errorMessage = response.message || 'An unknown error occurred'
      const isValidationError =
        errorMessage.toLowerCase().includes('already exists') ||
        errorMessage.toLowerCase().includes('duplicate') ||
        errorMessage.toLowerCase().includes('invalid') ||
        errorMessage.toLowerCase().includes('required')

      return Result.failure(
        new ApiError({
          type: isValidationError ? ApiErrorType.VALIDATION_ERROR : ApiErrorType.SERVER_ERROR,
          message: errorMessage,
          retryable: false,
          userMessage: errorMessage,
        })
      )
    }
  }
}
